"use client";

import { c as _c } from "react/compiler-runtime";
import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { WifiOff, Wifi } from "lucide-react";
import { ensurePushSubscribed } from "@/lib/push-client";
import BrandLogo from "@/components/tenant/BrandLogo";
import { useTenant } from "@/components/tenant/TenantProvider";
interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{
    outcome: "accepted" | "dismissed";
  }>;
}
const INSTALL_DISMISSED_KEY = "gc_pwa_install_dismissed";

/**
 * Mengelola sisi-klien PWA:
 *  - mendaftarkan service worker
 *  - menampilkan banner install kustom (beforeinstallprompt)
 *  - memberi tahu status offline/online
 * Aman untuk static export — semua dijalankan setelah hydrate di client.
 */
export default function PWAManager() {
  const $ = _c(22);
  const {
    brand_name
  } = useTenant();
  const [installEvent, setInstallEvent] = useState(null);
  const [showInstall, setShowInstall] = useState(false);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = [];
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  useEffect(_temp4, t0);
  let t1;
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => {
      const handler = e => {
        e.preventDefault();
        if (localStorage.getItem(INSTALL_DISMISSED_KEY) === "1") {
          return;
        }
        setInstallEvent(e as BeforeInstallPromptEvent);
        setShowInstall(true);
      };
      window.addEventListener("beforeinstallprompt", handler);
      window.addEventListener("appinstalled", () => {
        setShowInstall(false);
        setInstallEvent(null);
      });
      return () => window.removeEventListener("beforeinstallprompt", handler);
    };
    t2 = [];
    $[1] = t1;
    $[2] = t2;
  } else {
    t1 = $[1];
    t2 = $[2];
  }
  useEffect(t1, t2);
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = [];
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  useEffect(_temp5, t3);
  let t4;
  if ($[4] !== installEvent) {
    t4 = async () => {
      if (!installEvent) {
        return;
      }
      await installEvent.prompt();
      await installEvent.userChoice;
      setShowInstall(false);
      setInstallEvent(null);
    };
    $[4] = installEvent;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const handleInstall = t4;
  let t5;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = () => {
      localStorage.setItem(INSTALL_DISMISSED_KEY, "1");
      setShowInstall(false);
    };
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  const handleDismiss = t5;
  if (!showInstall) {
    return null;
  }
  const t6 = `Pasang aplikasi ${brand_name}`;
  let t7;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <div className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-xl bg-[var(--brand-primary)]"><BrandLogo variant="mark" width={28} height={28} className="rounded-md" /></div>;
    $[7] = t7;
  } else {
    t7 = $[7];
  }
  let t8;
  if ($[8] !== brand_name) {
    t8 = <p className="text-sm font-semibold text-[#0D0D0D]">Pasang {brand_name}</p>;
    $[8] = brand_name;
    $[9] = t8;
  } else {
    t8 = $[9];
  }
  let t9;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <p className="mt-0.5 text-[13px] leading-snug text-slate-500">{"Akses lebih cepat & dapat notifikasi langsung di layar utama."}</p>;
    $[10] = t9;
  } else {
    t9 = $[10];
  }
  let t10;
  if ($[11] !== handleInstall) {
    t10 = <button onClick={handleInstall} className="min-h-[40px] flex-1 rounded-lg bg-[var(--brand-primary)] px-4 text-sm font-semibold text-[var(--brand-on-primary,#fff)] transition active:scale-[0.97]">Pasang</button>;
    $[11] = handleInstall;
    $[12] = t10;
  } else {
    t10 = $[12];
  }
  let t11;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <button onClick={handleDismiss} className="min-h-[40px] rounded-lg px-4 text-sm font-medium text-slate-500 transition hover:bg-slate-50">Nanti</button>;
    $[13] = t11;
  } else {
    t11 = $[13];
  }
  let t12;
  if ($[14] !== t10) {
    t12 = <div className="mt-3 flex gap-2">{t10}{t11}</div>;
    $[14] = t10;
    $[15] = t12;
  } else {
    t12 = $[15];
  }
  let t13;
  if ($[16] !== t12 || $[17] !== t8) {
    t13 = <div className="flex items-start gap-3">{t7}<div className="min-w-0 flex-1">{t8}{t9}{t12}</div></div>;
    $[16] = t12;
    $[17] = t8;
    $[18] = t13;
  } else {
    t13 = $[18];
  }
  let t14;
  if ($[19] !== t13 || $[20] !== t6) {
    t14 = <div role="dialog" aria-label={t6} className="fixed inset-x-3 bottom-[calc(var(--nav-h)+0.5rem)] z-[1000] mx-auto max-w-md rounded-2xl border border-[#E4ECFC] bg-white p-4 shadow-[0_8px_30px_color-mix(in_srgb,var(--brand-primary)_18%,transparent)] sm:inset-x-auto sm:right-4 sm:bottom-4 sm:left-auto sm:w-96">{t13}</div>;
    $[19] = t13;
    $[20] = t6;
    $[21] = t14;
  } else {
    t14 = $[21];
  }
  return t14;
}
function _temp5() {
  let offlineTimer = null;
  let wasOffline = false;
  const goOffline = () => {
    if (offlineTimer) {
      clearTimeout(offlineTimer);
    }
    offlineTimer = setTimeout(() => {
      wasOffline = true;
      toast.warning("Koneksi terputus", {
        description: "Kami menyambungkan ulang otomatis saat internet kembali.",
        id: "pwa-offline",
        icon: <WifiOff className="h-5 w-5" />,
        duration: Infinity
      });
    }, 1200);
  };
  const goOnline = () => {
    if (offlineTimer) {
      clearTimeout(offlineTimer);
      offlineTimer = null;
    }
    toast.dismiss("pwa-offline");
    if (wasOffline) {
      wasOffline = false;
      toast.success("Koneksi kembali", {
        description: "Kamu tersambung lagi.",
        id: "pwa-online",
        icon: <Wifi className="h-5 w-5" />,
        duration: 2500
      });
    }
  };
  window.addEventListener("offline", goOffline);
  window.addEventListener("online", goOnline);
  return () => {
    if (offlineTimer) {
      clearTimeout(offlineTimer);
    }
    window.removeEventListener("offline", goOffline);
    window.removeEventListener("online", goOnline);
  };
}
function _temp4() {
  if (typeof window === "undefined" || !("serviceWorker" in navigator)) {
    return;
  }
  if (window.location.hostname === "localhost") {
    return;
  }
  const onLoad = _temp3;
  window.addEventListener("load", onLoad);
  return () => window.removeEventListener("load", onLoad);
}
function _temp3() {
  navigator.serviceWorker.register("/sw.js").then(_temp).catch(_temp2);
}
function _temp2(err) {
  console.warn("[PWA] SW registration failed:", err);
}
function _temp() {
  return ensurePushSubscribed();
}